import type { BunkerPoint, BunkerRoute } from "./bunkerGeometry";
import {
  BUNKER_WORLD,
  builtFootprintForSpace,
  nestedFootprintPercent,
  spaceFootprint,
  spaceRect,
} from "./bunkerGeometry";
import type { GameState, Space, WorkTask } from "../types";
import {
  motionForActivity,
  propForActivity,
  resolveSurvivorPresentation,
  stableSurvivorHash,
  type SurvivorMotion,
  type SurvivorPresentation,
} from "./survivorMotion";

export type PopulationZone = "room" | "passage";

export interface PopulationPlacement extends SurvivorPresentation {
  anchor: BunkerPoint;
  slot: number;
  targetSpaceId: string;
  zone: PopulationZone;
  passageId?: string;
}

export interface PopulationTransit {
  survivorId: string;
  fromSpaceId: string;
  toSpaceId: string;
  points: BunkerPoint[];
  cssPath: string;
  durationMs: number;
  facing: 1 | -1;
  motion: Extract<SurvivorMotion, "walk" | "climb">;
  key: string;
}

function allocateStableSlots(
  state: GameState,
  presentations: SurvivorPresentation[],
): Array<{ presentation: SurvivorPresentation; slot: number }> {
  const used = new Set<number>();
  const slotCount = Math.max(4, presentations.length);
  return [...presentations]
    .sort((left, right) => left.survivor.id.localeCompare(right.survivor.id))
    .map((presentation) => {
      const start = stableSurvivorHash(
        state.site.visual_seed,
        presentation.targetSpaceId,
        presentation.survivor.id,
      ) % slotCount;
      let slot = start;
      while (used.has(slot)) slot = (slot + 1) % slotCount;
      used.add(slot);
      return { presentation, slot };
    });
}

interface AmbientAnchor {
  spaceId: string;
  zone: PopulationZone;
  passageId?: string;
  point?: BunkerPoint;
}

function accessibleSpaces(state: GameState): Space[] {
  const candidates = state.site.spaces.filter((space) => (
    space.discovered
    && space.state === "excavated"
    && (!space.room || space.room.status !== "trashed")
  ));
  if (!candidates.length) return [];

  const byId = new Map(candidates.map((space) => [space.id, space]));
  const adjacency = new Map<string, string[]>();
  for (const connection of state.site.connections ?? []) {
    if (!byId.has(connection.from) || !byId.has(connection.to)) continue;
    adjacency.set(connection.from, [...(adjacency.get(connection.from) ?? []), connection.to]);
    adjacency.set(connection.to, [...(adjacency.get(connection.to) ?? []), connection.from]);
  }
  const start = byId.has("space-entrance") ? "space-entrance" : candidates[0]!.id;
  const reachable = new Set([start]);
  const queue = [start];
  while (queue.length) {
    const current = queue.shift()!;
    for (const next of adjacency.get(current) ?? []) {
      if (reachable.has(next)) continue;
      reachable.add(next);
      queue.push(next);
    }
  }
  return candidates
    .filter((space) => reachable.has(space.id))
    .sort((left, right) => left.y - right.y || left.x - right.x || left.id.localeCompare(right.id));
}

function routePoints(route: BunkerRoute): BunkerPoint[] {
  return [route.fromPoint, ...(route.bends ?? (route.bend ? [route.bend] : [])), route.toPoint];
}

function routeLength(points: BunkerPoint[]): number {
  return points.slice(1).reduce((total, point, index) => {
    const previous = points[index]!;
    return total + Math.abs(point.x - previous.x) + Math.abs(point.y - previous.y);
  }, 0);
}

function pointAlongRoute(points: BunkerPoint[], progress: number): BunkerPoint {
  const total = routeLength(points);
  if (total <= 0) return points[0] ?? { x: 50, y: 50 };
  let remaining = total * Math.max(0, Math.min(1, progress));
  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1]!;
    const to = points[index]!;
    const length = Math.abs(to.x - from.x) + Math.abs(to.y - from.y);
    if (remaining > length) {
      remaining -= length;
      continue;
    }
    const ratio = length > 0 ? remaining / length : 0;
    return { x: from.x + (to.x - from.x) * ratio, y: from.y + (to.y - from.y) * ratio };
  }
  return points.at(-1)!;
}

function ambientAnchors(
  state: GameState,
  routes: BunkerRoute[],
): AmbientAnchor[] {
  const spaces = accessibleSpaces(state);
  const accessibleIds = new Set(spaces.map((space) => space.id));
  const roomAnchors: AmbientAnchor[] = spaces.map((space) => ({
    spaceId: space.id,
    zone: "room",
  }));
  const passageAnchors: AmbientAnchor[] = routes
    .filter((route) => (
      route.state === "open"
      && accessibleIds.has(route.from)
      && accessibleIds.has(route.to)
      && routeLength(routePoints(route)) >= 4
    ))
    .sort((left, right) => left.id.localeCompare(right.id))
    .map((route) => ({
      spaceId: route.from,
      zone: "passage" as const,
      passageId: route.id,
      point: pointAlongRoute(
        routePoints(route),
        .42 + (stableSurvivorHash(state.site.visual_seed, route.id, "passage-anchor") % 17) / 100,
      ),
    }));

  /* Rooms remain the primary destination; real, long passages add one lived-in
     circulation point apiece rather than turning every doorway into a crowd. */
  return [...roomAnchors, ...passageAnchors];
}

function isAmbientUnassigned(presentation: SurvivorPresentation): boolean {
  const survivor = presentation.survivor;
  if (survivor.current_activity) return survivor.current_activity.kind === "idle";
  return !survivor.assignment
    && survivor.fatigue < 70
    && survivor.thirst < 66
    && survivor.hunger < 66
    && survivor.morale > 48;
}

function moveAmbientPresentation(
  state: GameState,
  presentation: SurvivorPresentation,
  anchor: AmbientAnchor,
): SurvivorPresentation {
  const target = state.site.spaces.find((space) => space.id === anchor.spaceId);
  const activity = anchor.zone === "passage"
    ? "idle"
    : target?.room?.type === "entrance"
      ? "watch"
      : "idle";
  const survivor = presentation.survivor;
  const facing = stableSurvivorHash(
    state.site.visual_seed,
    survivor.id,
    anchor.spaceId,
    anchor.passageId,
    activity,
  ) % 2 === 0 ? 1 : -1;
  return {
    ...presentation,
    activity,
    motion: motionForActivity(activity, survivor),
    prop: propForActivity(activity, survivor),
    targetSpaceId: anchor.spaceId,
    facing,
    transitionKey: [
      state.site.number,
      state.site.visual_seed,
      survivor.id,
      survivor.status,
      "ambient",
      anchor.spaceId,
      anchor.passageId ?? "room",
    ].join(":"),
  };
}

export function roomWorkstationAnchor(
  space: Space,
  slot: number,
  task?: WorkTask,
): BunkerPoint {
  const rect = spaceRect(space);
  const capacity = spaceFootprint(space);
  const footprint = builtFootprintForSpace(
    space,
    task?.target_footprint ?? space.room?.footprint,
  );
  const nested = nestedFootprintPercent(footprint, capacity);
  const builtWidth = rect.width * nested.width / 100;
  const builtHeight = rect.height * nested.height / 100;
  const column = slot % 4;
  const row = Math.floor(slot / 4);
  const tallRoom = footprint.height > 1;
  const peopleHeight = tallRoom ? .46 : .8;
  const peopleBottom = tallRoom ? .03 : .02;
  return {
    x: rect.left + builtWidth * (.185 + column * .2025),
    y: rect.top + builtHeight * (1 - peopleBottom - row * .12 * peopleHeight),
  };
}

export function resolveBunkerPopulation(
  state: GameState,
  routes: BunkerRoute[] = [],
): PopulationPlacement[] {
  const homes = state.survivors
    .map((survivor) => resolveSurvivorPresentation(state, survivor))
    .filter((presentation) => (
      presentation.survivor.alive
      && presentation.survivor.status === "home"
      && presentation.targetSpaceId
    ));
  const ambient = homes
    .filter(isAmbientUnassigned)
    .sort((left, right) => left.survivor.id.localeCompare(right.survivor.id));
  const anchors = ambientAnchors(state, routes);
  const anchorOffset = anchors.length
    ? stableSurvivorHash(state.site.visual_seed, state.site.number, "ambient-population") % anchors.length
    : 0;
  const ambientBySurvivor = new Map(ambient.map((presentation, index) => [
    presentation.survivor.id,
    anchors.length ? anchors[(anchorOffset + index) % anchors.length]! : null,
  ]));
  const resolved = homes.map((presentation) => {
    const anchor = ambientBySurvivor.get(presentation.survivor.id);
    return anchor
      ? { presentation: moveAmbientPresentation(state, presentation, anchor), anchor }
      : { presentation, anchor: null };
  });

  const bySpace = new Map<string, SurvivorPresentation[]>();
  const passages: PopulationPlacement[] = [];
  for (const entry of resolved) {
    const { presentation, anchor } = entry;
    if (!presentation.targetSpaceId) continue;
    if (anchor?.zone === "passage" && anchor.point) {
      passages.push({
        ...presentation,
        targetSpaceId: presentation.targetSpaceId,
        anchor: anchor.point,
        passageId: anchor.passageId,
        slot: 0,
        zone: "passage",
      });
      continue;
    }
    bySpace.set(presentation.targetSpaceId, [
      ...(bySpace.get(presentation.targetSpaceId) ?? []),
      presentation,
    ]);
  }

  const spaces = new Map(state.site.spaces.map((space) => [space.id, space]));
  const rooms = [...bySpace.entries()].flatMap(([spaceId, presentations]) => {
    const space = spaces.get(spaceId);
    if (!space || !space.discovered) return [];
    const task = state.tasks.find((item) => item.space_id === spaceId);
    return allocateStableSlots(state, presentations).map(({ presentation, slot }) => ({
      ...presentation,
      targetSpaceId: spaceId,
      anchor: roomWorkstationAnchor(space, slot, task),
      slot,
      zone: "room" as const,
    }));
  });
  return [...rooms, ...passages].sort((left, right) => left.survivor.id.localeCompare(right.survivor.id));
}

function routeTraversalPoints(route: BunkerRoute, fromSpaceId: string): BunkerPoint[] {
  const points = [route.fromPoint, ...(route.bends ?? (route.bend ? [route.bend] : [])), route.toPoint];
  return route.from === fromSpaceId ? points : points.reverse();
}

function samePoint(left: BunkerPoint, right: BunkerPoint): boolean {
  return Math.abs(left.x - right.x) < .001 && Math.abs(left.y - right.y) < .001;
}

function compressPoints(points: BunkerPoint[]): BunkerPoint[] {
  const compressed: BunkerPoint[] = [];
  for (const point of points) {
    const previous = compressed.at(-1);
    if (previous && samePoint(previous, point)) continue;
    const before = compressed.at(-2);
    if (before && previous) {
      const sameX = Math.abs(before.x - previous.x) < .001 && Math.abs(previous.x - point.x) < .001;
      const sameY = Math.abs(before.y - previous.y) < .001 && Math.abs(previous.y - point.y) < .001;
      if (sameX || sameY) {
        compressed[compressed.length - 1] = point;
        continue;
      }
    }
    compressed.push(point);
  }
  return compressed;
}

export function populationRoutePoints(
  routes: BunkerRoute[],
  fromSpaceId: string,
  toSpaceId: string,
): BunkerPoint[] | null {
  if (fromSpaceId === toSpaceId) return [];
  const adjacency = new Map<string, Array<{ next: string; route: BunkerRoute }>>();
  for (const route of routes) {
    adjacency.set(route.from, [...(adjacency.get(route.from) ?? []), { next: route.to, route }]);
    adjacency.set(route.to, [...(adjacency.get(route.to) ?? []), { next: route.from, route }]);
  }
  for (const edges of adjacency.values()) edges.sort((left, right) => left.route.id.localeCompare(right.route.id));

  const queue = [fromSpaceId];
  const previous = new Map<string, { space: string; route: BunkerRoute }>();
  const visited = new Set([fromSpaceId]);
  while (queue.length) {
    const current = queue.shift()!;
    if (current === toSpaceId) break;
    for (const edge of adjacency.get(current) ?? []) {
      if (visited.has(edge.next)) continue;
      visited.add(edge.next);
      previous.set(edge.next, { space: current, route: edge.route });
      queue.push(edge.next);
    }
  }
  if (!visited.has(toSpaceId)) return null;

  const steps: Array<{ from: string; route: BunkerRoute }> = [];
  let cursor = toSpaceId;
  while (cursor !== fromSpaceId) {
    const entry = previous.get(cursor);
    if (!entry) return null;
    steps.push({ from: entry.space, route: entry.route });
    cursor = entry.space;
  }
  steps.reverse();
  return compressPoints(steps.flatMap((step) => routeTraversalPoints(step.route, step.from)));
}

function pathDistance(points: BunkerPoint[]): { horizontal: number; vertical: number } {
  return points.slice(1).reduce(
    (distance, point, index) => {
      const previous = points[index]!;
      distance.horizontal += Math.abs(point.x - previous.x) / 100 * BUNKER_WORLD.width;
      distance.vertical += Math.abs(point.y - previous.y) / 100 * BUNKER_WORLD.height;
      return distance;
    },
    { horizontal: 0, vertical: 0 },
  );
}

function cssMotionPath(points: BunkerPoint[]): string {
  return points.map((point, index) => {
    const x = point.x / 100 * BUNKER_WORLD.width;
    const y = point.y / 100 * BUNKER_WORLD.height;
    return `${index === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`;
  }).join(" ");
}

export function buildPopulationTransit(
  from: PopulationPlacement,
  to: PopulationPlacement,
  routes: BunkerRoute[],
): PopulationTransit | null {
  const middle = populationRoutePoints(routes, from.targetSpaceId, to.targetSpaceId);
  if (!middle) return null;
  const points = compressPoints([from.anchor, ...middle, to.anchor]);
  if (points.length < 2) return null;
  const distance = pathDistance(points);
  const total = distance.horizontal + distance.vertical;
  const firstHorizontal = points.slice(1).find((point, index) => (
    Math.abs(point.x - points[index]!.x) > .001
  ));
  const firstHorizontalIndex = firstHorizontal ? points.indexOf(firstHorizontal) : -1;
  const facing = firstHorizontal && firstHorizontalIndex > 0
    ? firstHorizontal.x >= points[firstHorizontalIndex - 1]!.x ? 1 : -1
    : to.facing;
  return {
    survivorId: to.survivor.id,
    fromSpaceId: from.targetSpaceId,
    toSpaceId: to.targetSpaceId,
    points,
    cssPath: cssMotionPath(points),
    durationMs: Math.round(Math.max(900, Math.min(4800, total * 15))),
    facing,
    motion: distance.vertical > distance.horizontal * .72 ? "climb" : "walk",
    key: `${from.transitionKey}->${to.transitionKey}`,
  };
}

export function bunkerPopulationSignature(placements: PopulationPlacement[]): string {
  return [...placements]
    .sort((left, right) => left.survivor.id.localeCompare(right.survivor.id))
    .map((placement) => `${placement.survivor.id}:${placement.transitionKey}`)
    .join("|");
}
